import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfig } from '@lore/config';
import storage, { SCHOOL_DRAFT_KEY } from '@/utils/storage';

const FOUR_HOURS = 4 * 60 * 60 * 1000;

/**
 * @typedef {Object} PotentialSite
 * @property {number} id
 * @property {string} name
 * @property {string} [phoneNumber]
 * @property {string} [website]
 * @property {string} [addressLine1]
 * @property {string} [addressLine2]
 * @property {string} [city]
 * @property {string} [state]
 * @property {string} [zipCode]
 * @property {number} [submittedByUserId]
 * @property {string} reviewStatus
 * @property {string} [adminNotes]
 * @property {string} [createdAt]
 * @property {string} [updatedAt]
 * @property {PotentialSite} data - Self-referencing data property for component compatibility
 * @export
 */

/**
 * Custom hook for managing potential sites.
 * GET requests are cached for 4 hours.
 * POST, PATCH, and DELETE operations clear the cache.
 * @param {Object} [params]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=20]
 * @param {boolean} [params.bypassUserScope=false]
 * @returns {import('@tanstack/react-query').UseQueryResult & { data: PotentialSite[], total: number, page: number, pageSize: number, create: Function, update: Function, destroy: Function }}
 */
export default function usePotentialSites(params = {}) {
  const {
    page = 1,
    pageSize = 20,
    bypassUserScope = false,
  } = params;

  const config = useConfig();
  const { apiRoot, headers } = config.connections.default;
  const queryClient = useQueryClient();

  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('pageSize', pageSize);
  if (bypassUserScope) queryParams.append('bypassUserScope', bypassUserScope);

  const queryKey = ['potential_sites', 'list', { page, pageSize, bypassUserScope }];

  const query = useQuery({
    queryKey,
    queryFn: () => {
      const url = `${apiRoot}/core/potential_sites?${queryParams.toString()}`;
      return fetch(url, {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch potential sites');
        }
        return res.json();
      });
    },
    staleTime: FOUR_HOURS,
    cacheTime: FOUR_HOURS,
    refetchOnWindowFocus: false,
    select: (response) => {
      if (!response || !response.data) return { data: [], total: 0, page, pageSize };
      return {
        ...response,
        data: response.data.map((site) => ({
          ...site,
          data: site
        }))
      };
    },
  });


  const createMutation = useMutation({
    mutationFn: (data) => {
      const url = `${apiRoot}/core/potential_sites`;
      return fetch(url, {
        method: 'POST',
        headers: {
          ...headers(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to create potential site');
        }
        return res.json();
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['potential_sites']);
      storage.delete(SCHOOL_DRAFT_KEY);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }) => {
      const url = `${apiRoot}/core/potential_sites/${id}`;
      return fetch(url, {
        method: 'PATCH',
        headers: {
          ...headers(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to update potential site');
        }
        return res.json();
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['potential_sites']);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => {
      const url = `${apiRoot}/core/potential_sites/${id}`;
      return fetch(url, {
        method: 'DELETE',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to delete potential site');
        }
        return res;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['potential_sites']);
      storage.delete(SCHOOL_DRAFT_KEY);
    },
  });

  return {
    ...query,
    data: query.data?.data || [],
    total: query.data?.total || 0,
    page: query.data?.page || page,
    pageSize: query.data?.pageSize || pageSize,

    // Actions
    create: (data) => createMutation.mutate(data),
    update: (id, data) => updateMutation.mutate({ id, ...data }),
    destroy: (id) => deleteMutation.mutate(id),

    // Mutation states
    isCreating: createMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,

    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,

    createSuccess: createMutation.isSuccess,
    updateSuccess: updateMutation.isSuccess,
    deleteSuccess: deleteMutation.isSuccess,
  };
}

/**
 * Custom hook for fetching a single potential site.
 * @param {number} id
 * @param {Object} [params]
 * @param {boolean} [params.bypassUserScope=false]
 * @returns {import('@tanstack/react-query').UseQueryResult & { data: PotentialSite, update: Function, destroy: Function }}
 */
export function usePotentialSite(id, params = {}) {
  const {
    bypassUserScope = false,
  } = params;

  const config = useConfig();
  const { apiRoot, headers } = config.connections.default;
  const queryClient = useQueryClient();

  const queryParams = new URLSearchParams();
  if (bypassUserScope) queryParams.append('bypassUserScope', bypassUserScope);

  const queryKey = ['potential_sites', 'byId', id, { bypassUserScope }];

  const query = useQuery({
    queryKey,
    queryFn: () => {
      const url = `${apiRoot}/core/potential_sites/${id}?${queryParams.toString()}`;
      return fetch(url, {
        method: 'GET',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch potential site');
        }
        return res.json();
      });
    },
    staleTime: FOUR_HOURS,
    cacheTime: FOUR_HOURS,
    refetchOnWindowFocus: false,
    enabled: !!id,
    select: (site) => site ? { ...site, data: site } : null,
  });

  const updateMutation = useMutation({
    mutationFn: (data) => {
      const url = `${apiRoot}/core/potential_sites/${id}`;
      return fetch(url, {
        method: 'PATCH',
        headers: {
          ...headers(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to update potential site');
        }
        return res.json();
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['potential_sites']);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      const url = `${apiRoot}/core/potential_sites/${id}`;
      return fetch(url, {
        method: 'DELETE',
        headers: headers(),
      }).then((res) => {
        if (!res.ok) {
          throw new Error('Failed to delete potential site');
        }
        return res;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['potential_sites']);
    },
  });

  return {
    ...query,
    data: query.data,
    update: (data) => updateMutation.mutate(data),
    destroy: () => deleteMutation.mutate(),

    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading,

    updateError: updateMutation.error,
    deleteError: deleteMutation.error,

    updateSuccess: updateMutation.isSuccess,
    deleteSuccess: deleteMutation.isSuccess,
  };
}
